import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Tag, ArrowLeft, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const BlogArticlePage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/posts/${slug}`).then(r => r.ok ? r.json() : null),
      fetch(`${API}/api/posts?status=published`).then(r => r.json()),
      fetch(`${API}/api/categories`).then(r => r.json())
    ]).then(([p, all, cats]) => {
      setPost(p);
      setCategories(cats);
      if (p) setRelated(all.filter(a => a.id !== p.id && a.category_id === p.category_id).slice(0, 2));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const getCatName = (id) => categories.find(c => c.id === id)?.name || '';
  const scrollToContact = () => { window.location.href = '/#contact'; };

  if (loading) return <div className="min-h-screen bg-cream flex items-center justify-center"><p className="text-stone-500">Nalaganje...</p></div>;
  if (!post) return <div className="min-h-screen bg-cream flex items-center justify-center"><p className="text-stone-500">Objava ni najdena.</p></div>;

  const imgSrc = post.featured_image?.startsWith('/api') ? `${API}${post.featured_image}` : post.featured_image;

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>{post.title} | Val Dotika Blog</title>
        {post.meta_description && <meta name="description" content={post.meta_description} />}
        {post.meta_keywords && <meta name="keywords" content={post.meta_keywords} />}
      </Helmet>
      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <main className="pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1 text-violet-600 hover:text-violet-800 text-sm font-medium mb-6">
            <ArrowLeft size={14} /> Nazaj na blog
          </Link>

          {imgSrc && (
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-6 shadow-lg">
              <img src={imgSrc} alt={post.title} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="flex items-center gap-3 mb-3">
            {getCatName(post.category_id) && <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">{getCatName(post.category_id)}</span>}
            <span className="text-xs text-stone-400 flex items-center gap-1"><Calendar size={12} />{new Date(post.created_at).toLocaleDateString('sl-SI')}</span>
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-stone-800 font-bold mb-4 leading-tight" data-testid="article-title">{post.title}</h1>
          {post.excerpt && <p className="text-lg text-stone-600 mb-6 italic">{post.excerpt}</p>}

          <div className="blog-content text-stone-700 text-base leading-relaxed" data-testid="article-content" dangerouslySetInnerHTML={{ __html: post.content }} />

          {post.tags && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-stone-200">
              <Tag size={14} className="text-stone-400 mt-1" />
              {post.tags.map((tag, i) => <span key={i} className="text-xs bg-stone-100 text-stone-600 px-2 py-1 rounded-full">{tag}</span>)}
            </div>
          )}

          {/* CTA */}
          <div className="mt-10 bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl p-6 text-white text-center" data-testid="article-cta">
            <p className="font-serif text-xl mb-3">Če želiš nežno podporo zase in za svojega dojenčka...</p>
            <Button onClick={scrollToContact} className="bg-white text-violet-700 hover:bg-violet-50 rounded-full px-6 py-3 font-semibold transition-all">
              Rezerviraj termin <ArrowRight size={16} className="ml-1" />
            </Button>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-10">
              <h3 className="font-serif text-2xl text-stone-800 font-bold mb-4">Povezane objave</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {related.map(r => (
                  <Link key={r.id} to={`/blog/${r.slug}`} className="group bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all border border-stone-100">
                    <h4 className="font-serif text-lg text-stone-800 font-bold group-hover:text-violet-700 transition-colors">{r.title}</h4>
                    <p className="text-stone-500 text-sm mt-1 line-clamp-2">{r.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogArticlePage;
